import React from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { useCustomer } from '@techstyle/react-accounts';
import { useBreakpoint } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import {
  findSizeSetProducts,
  findSizeSingleProducts,
} from '../../utils/productDetail';
import { getAvailableQuantity } from '../../utils/productQuantity';
import { useMembership } from '../../utils/useMembership';
import { useFooterContext } from '../Footer/FooterProvider';
import { Component as ProductDetailCTA } from '../ProductDetailCTA';
import { useProductDetailContext } from '../ProductDetailLayout/ProductDetailContext';
import {
  LearnMoreLinkInline,
  OptionToolTip,
  ProductDetailPriceModeLegalText,
} from '../ProductDetailPriceModeLegalText/ProductDetailPriceModeLegalText';
import { Component as ProductDetailVipToggleButtons } from '../ProductDetailVipToggleButtons';

const StyledProductDetailCTA = styled(ProductDetailCTA)`
  margin-top: ${props => props.theme.spacing.small}px;
  & ${ProductDetailCTA.ProductDetailAddToCartButton} {
    ${({ shouldColorByPromo, theme }) => shouldColorByPromo && css``}
  }
`;

const FloatingProductDetailCTA = styled(ProductDetailCTA)`
  bottom: 10px;
  width: calc(100% - 32px);
  position: fixed;
  z-index: 1;

  & ${ProductDetailCTA.ProductDetailAddToCartButton} {
    ${({ shouldColorByPromo, theme }) => shouldColorByPromo && css``}
  }
`;

const StyledInactiveStyledProductDetailCTA = styled(
  StyledProductDetailCTA
).attrs(({ secondaryDark = true }) => ({
  secondaryDark,
}))`
  ${ProductDetailCTA.ProductDetailAddToCartButton} {
  }
`;

const StyledProductDetailVipInactiveToggleButtons = styled(
  ProductDetailVipToggleButtons
)`
  ${OptionToolTip},
  ${LearnMoreLinkInline},
  ${ProductDetailVipToggleButtons.VipText},
  ${ProductDetailVipToggleButtons.StyledPrice},
  ${ProductDetailVipToggleButtons.GuestText} {
    color: ${({ theme }) => theme.colors.subdued};
  }

  ${ProductDetailCTA.ProductDetailAddToCartButton} {
    background-color: ${({ theme }) => theme.colors.subdued};
    border-color: ${({ theme }) => theme.colors.subdued};
    color: ${({ theme }) => theme.colors.white};
  }
`;

function ProductDetailCTAs({
  allowExclusiveCheckout,
  cart,
  className,
  constructorButtonLabel,
  handleRegCtaAddToCart,
  handleToggleButtonClick,
  hideRegCta,
  isCartButtonDisabled: isCartButtonDisabledFromProps,
  isComingSoonProduct,
  isExclusive,
  isHiddenFromGrid,
  isSoldOut,
  isXtraVipBox,
  isPreorderable,
  isProductSet,
  membershipResolved,
  membershipItem,
  product,
  promos,
  selections,
  shouldNotShowCtaForSports,
  shouldShowPromoPrice,
  showToastMessage,
  shouldShowNewProductPricing,
}) {
  const isBorderfreeCustomer = useSelector(
    state => state.borderfree.isBorderfreeCustomer
  );
  const { influencerInfo, isInfluencer } = useCustomer();
  const { isVip } = useMembership();
  const { isMobile } = useBreakpoint();
  const footerRef = useFooterContext();
  const [isFooterVisible, setIsFooterVisible] = React.useState(false);
  const {
    singleCTA: { enabled: pdpSingleCTAFlagEnabled },
  } = useProductDetailContext();

  const showToast = () => {
    showToastMessage(
      <FormattedMessage
        defaultMessage="Sport items are for VIPS only"
        id="site_pdp.sport_items_are_for_vips_only"
      />
    );
  };

  React.useEffect(() => {
    const scrollHandler = () => {
      if (
        window?.pageYOffset + window?.innerHeight >=
        footerRef.current?.offsetTop
      ) {
        setIsFooterVisible(true);
      } else {
        setIsFooterVisible(false);
      }
    };

    window.addEventListener('scroll', scrollHandler);

    return () => window.removeEventListener('scroll', scrollHandler);
  }, [footerRef]);

  if (!product) {
    return null;
  }

  const hasNoInfluencerPromo =
    isInfluencer && !influencerInfo.userHasActivePromo;

  const isCartButtonDisabled = isHiddenFromGrid
    ? true
    : isCartButtonDisabledFromProps || hasNoInfluencerPromo;

  const isUserAllowedToSeeDualCta =
    membershipResolved &&
    !isVip &&
    !isInfluencer &&
    !membershipItem &&
    ((isProductSet && !isXtraVipBox) || !isProductSet) &&
    !isSoldOut;

  const sizeProducts = isProductSet
    ? findSizeSetProducts(selections)
    : findSizeSingleProducts(selections);

  const availableQuantity = isSoldOut
    ? 0
    : getAvailableQuantity(sizeProducts, product, isPreorderable);

  const shouldRenderFloatingBtn = isMobile && !isVip && !isFooterVisible;

  const SingleButtonCtaComponent = shouldRenderFloatingBtn
    ? FloatingProductDetailCTA
    : StyledProductDetailCTA;

  // Dual button
  if (isUserAllowedToSeeDualCta || hideRegCta) {
    const { default_unit_price: vipPrice, retail_unit_price: guestPrice } =
      product;

    const vipAmount = !shouldShowPromoPrice ? vipPrice : promos.promo_price;

    return shouldNotShowCtaForSports ? (
      <StyledProductDetailVipInactiveToggleButtons
        allowExclusiveCheckout={allowExclusiveCheckout}
        availablePreorderQuantity={0}
        availableQuantity={availableQuantity}
        cartButtonDisabled={isCartButtonDisabled}
        className={className}
        guestAmount={guestPrice}
        isExclusive={isExclusive}
        isBorderfreeCustomer={isBorderfreeCustomer}
        isPreorderable={isPreorderable}
        isSoldOut={isProductSet ? isSoldOut : false}
        isVip={isVip}
        isXtraVipBox={isXtraVipBox}
        onClick={handleToggleButtonClick}
        vipAmount={vipAmount}
        shouldNotShowCtaForSports={shouldNotShowCtaForSports}
        shouldShowPromoPrice={shouldShowPromoPrice}
        showToastMessage={showToastMessage}
      />
    ) : (
      <ProductDetailVipToggleButtons
        allowExclusiveCheckout={allowExclusiveCheckout}
        availablePreorderQuantity={0}
        availableQuantity={availableQuantity}
        cartButtonDisabled={isCartButtonDisabled}
        className={className}
        guestAmount={guestPrice}
        memberPrice={vipPrice}
        isExclusive={isExclusive}
        isBorderfreeCustomer={isBorderfreeCustomer}
        isPreorderable={isPreorderable}
        isSoldOut={isProductSet ? isSoldOut : false}
        isProductSet={isProductSet}
        isVip={isVip}
        isXtraVipBox={isXtraVipBox}
        onClick={handleToggleButtonClick}
        vipAmount={vipAmount}
        shouldShowPromoPrice={shouldShowPromoPrice}
        shouldShowNewProductPricing={shouldShowNewProductPricing}
      />
    );
  }

  // Everyone else (standard single CTA)
  const renderSingleButtonText = () => {
    return isProductSet ? (
      pdpSingleCTAFlagEnabled ? (
        <FormattedMessage
          id="site_pdp.single_cta_add_set_to_bag_cta"
          defaultMessage="Member Add Set to Bag"
        />
      ) : (
        <FormattedMessage
          id="site_pdp.add_set_to_bag_cta"
          defaultMessage="Add Set to Bag"
        />
      )
    ) : (
      <>
        <FormattedMessage
          id="site_pdp.Add_to_bag_cta"
          defaultMessage="Add to Bag"
        />
      </>
    );
  };

  return shouldNotShowCtaForSports ? (
    <StyledInactiveStyledProductDetailCTA
      allowExclusiveCheckout={allowExclusiveCheckout}
      availablePreorderQuantity={0}
      availableQuantity={availableQuantity}
      cartButtonDisabled={isCartButtonDisabled || isSoldOut}
      isExclusive={isExclusive}
      isPreorderable={isPreorderable}
      isSoldOut={isProductSet ? isSoldOut : false}
      isWaitlistable={false}
      isXtraVipBox={isXtraVipBox}
      isComingSoonProduct={isComingSoonProduct}
      onAddToCart={showToast}
      shouldColorByPromo={shouldShowPromoPrice}
      shouldNotShowCtaForSports={shouldNotShowCtaForSports}
    >
      {renderSingleButtonText()}
    </StyledInactiveStyledProductDetailCTA>
  ) : (
    <>
      <SingleButtonCtaComponent
        allowExclusiveCheckout={allowExclusiveCheckout}
        availablePreorderQuantity={0}
        availableQuantity={availableQuantity}
        cartButtonDisabled={isCartButtonDisabled || isSoldOut}
        constructorButtonLabel="add_to_bag"
        isExclusive={isExclusive}
        isPreorderable={isPreorderable}
        isSoldOut={isProductSet ? isSoldOut : false}
        isWaitlistable={false}
        isXtraVipBox={isXtraVipBox}
        isComingSoonProduct={isComingSoonProduct}
        onAddToCart={handleRegCtaAddToCart}
        shouldColorByPromo={shouldShowPromoPrice}
        shouldNotShowCtaForSports={shouldNotShowCtaForSports}
      >
        {renderSingleButtonText()}
      </SingleButtonCtaComponent>
      <ProductDetailPriceModeLegalText
        shouldNotShowCtaForSports={shouldNotShowCtaForSports}
      />
    </>
  );
}

ProductDetailCTAs.propTypes = {
  allowExclusiveCheckout: PropTypes.bool,
  className: PropTypes.string,
  cart: PropTypes.object,
  handleRegCtaAddToCart: PropTypes.func,
  handleToggleButtonClick: PropTypes.func,
  hideRegCta: PropTypes.bool,
  isCartButtonDisabled: PropTypes.bool,
  isComingSoonProduct: PropTypes.bool,
  isExclusive: PropTypes.bool,
  isHiddenFromGrid: PropTypes.bool,
  isProductSet: PropTypes.bool,
  isSoldOut: PropTypes.bool,
  isXtraVipBox: PropTypes.bool,
  isPreorderable: PropTypes.bool,
  membershipResolved: PropTypes.bool,
  membershipItem: PropTypes.object,
  product: PropTypes.object,
  promos: PropTypes.object,
  selections: PropTypes.object,
  shouldNotShowCtaForSports: PropTypes.bool,
  shouldShowPromoPrice: PropTypes.bool,
  showToastMessage: PropTypes.func,
};

export default ProductDetailCTAs;
